import { graphql } from "@acto/gql";
import { useMemo } from "react";
import { useQuery } from "urql";
import { DataAccessResult } from "@acto/taskpane/core/data-access/types";
import { isFetchingResult } from "@acto/taskpane/core/data-access/isFetchingResult";
import { ProductExclusion } from "@acto/taskpane/core/domain";

export const ClientProductExclusions = graphql(`
  fragment ClientProductExclusions on Client {
    __typename
    id
    productExclusions {
      id
      __typename
      clientId
      productId
      productName {
        locale
        value
      }
      reason
      reasonText
      validTo
    }
  }
`);

const ProductExclusionsQuery = graphql(`
  query GetProductExclusions($clientId: ID!) {
    clients(first: 1, filter: { id: $clientId }) {
      __typename
      edges {
        __typename
        node {
          ...ClientProductExclusions
        }
      }
    }
  }
`);

export function useProductExclusions(clientId?: string): DataAccessResult<ProductExclusion> {
  const [result] = useQuery({
    query: ProductExclusionsQuery,
    variables: { clientId: clientId ?? "" },
    pause: !clientId,
  });

  const matchesRequest = result.operation?.variables.clientId === clientId;
  const data = matchesRequest ? result.data : undefined;
  const error = matchesRequest ? result.error : undefined;
  const isFetching = isFetchingResult(result);

  // Other queries cache this client without its exclusions, so graphcache can answer with the
  // client and a null list while the network request is still out. Null there means unknown, not empty.
  const exclusions = useMemo(() => {
    const client = data?.clients.edges[0]?.node;
    if (!data || (client && client.productExclusions == null && isFetching)) return undefined;
    return (client?.productExclusions ?? []).map(ProductExclusion.fromGraphql);
  }, [data, isFetching]);

  return {
    data: exclusions,
    isFetching,
    isPending: exclusions === undefined && isFetching,
    error,
  };
}
